import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { usePortfolioSnapshot } from "../../hooks/usePortfolioSnapshot";
import type { PortfolioSnapshotResponse } from "../../lib/api";
import { formatAddress } from "../../lib/format";
import { PortfolioKpis } from "./PortfolioKpis";
import { PortfolioSkeleton } from "./PortfolioSkeleton";
import { PositionsTable } from "./PositionsTable";
import { UnquotedBlock } from "./UnquotedBlock";

/**
 * Portfolio section — snapshot for the worker's default wallet
 * (`KAM_WALLET_ADDRESS` in wrangler.jsonc). Fires on mount; the
 * resolved wallet is surfaced in the header so the user sees which
 * address the data belongs to.
 */
export function PortfolioSection() {
	const { data, error, isLoading } = usePortfolioSnapshot();

	return (
		<section className="space-y-4">
			<SectionHeader
				title="Portfolio · where the money is"
				subtitle={
					data
						? `${data.positionCount} position${data.positionCount === 1 ? "" : "s"} · joined with live quotes`
						: "joined with live quotes"
				}
				trailing={
					data && (
						<span
							className="font-mono text-[11px] text-slate-500"
							title={data.wallet}
						>
							{formatAddress(data.wallet)}
						</span>
					)
				}
			/>

			{isLoading && <PortfolioSkeleton />}
			{error && <ErrorBanner>{(error as Error).message}</ErrorBanner>}
			{data && <PortfolioBody data={data} />}
		</section>
	);
}

function PortfolioBody({ data }: { data: PortfolioSnapshotResponse }) {
	const showCashRow = BigInt(data.cashUsdcRaw) > 0n;
	return (
		<div className="space-y-4">
			<PortfolioKpis data={data} />
			<PositionsTable
				positions={data.positions}
				cashUsdcRaw={showCashRow ? data.cashUsdcRaw : null}
				totalValueUsdcRaw={data.totalValueUsdcRaw}
			/>
			{data.unquotedEntries.length > 0 && (
				<UnquotedBlock entries={data.unquotedEntries} />
			)}
		</div>
	);
}
